import type { Content } from '@tiptap/core';
import { mergeAttributes, Node, PasteRule } from '@tiptap/core';
import { ReactNodeViewRenderer, ReactRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import { Suggestion, SuggestionPluginKey } from '@tiptap/suggestion';
import type { Instance } from 'tippy.js';
import tippy from 'tippy.js';
import { VariablesList } from '../../components/VariablesList';
import type { VariablesListRef } from '../../components/VariablesList';
import { isVariableFolder, type VariableEntity, type VariableNode } from 'services/entities/VariablesEntity';
import { VariableNodeView } from './VariableNodeView';
import { PreviewVariableNodeView } from './PreviewVariableNodeView';
import { findVariableForChip, formatVariable } from './utils';
import { PreviewContentVariableNodeView } from './PreviewContentVariableNodeView.tsx';
import type { CustomContextMenuOptions } from '../../../CustomContextMenu/CustomContextMenuContainer.tsx';

export type VariableChipAttrs = {
  name: string;
  friendlyName?: string | null;
  objectId?: string | null;
  path?: string | null;
  template?: string | null;
  isConflict?: boolean;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variables: {
      insertVariable: (variable: string | VariableChipAttrs, range?: { from: number; to: number }) => ReturnType;
      setVariablesToStorage: (variables: VariableEntity[]) => ReturnType;
    };
  }
}

type BaseVariablesExtensionsProps = {
  variables: VariableEntity[];
  documentCurrencyCode?: string;
  menuItems?: CustomContextMenuOptions;
  disableSuggestions?: boolean;
};

type BaseVariablesExtensionsStorage = {
  variables: VariableEntity[];
  // Set imperatively by the table cell that owns this editor instance
  // (mirrors the Comments extension's onThreadClick) — the extension
  // itself has no row/table context to re-derive CRM rows with.
  onArrayVariableInserted: ((variable: VariableEntity, range?: { from: number; to: number }, needsSpace?: boolean) => void) | null;
};

export const PVAR_REGEX = /\[\[pvar:([\s\S]+)\]\]/g;

type ClipboardVariablePayload = {
  data: {
    name: string;
    friendlyName: string;
    type: string;
    value: unknown;
    variables: unknown[];
    path: string;
  };
  isTemplateEditor: boolean;
};

type VariablePickerState = {
  virtualQuery: string;
  latestProps: SuggestionProps<VariableEntity> | null;
  component: ReactRenderer<VariablesListRef, SuggestionProps<VariableEntity>> | null;
};

function filterVariables(variables: VariableEntity[], query: string): VariableEntity[] {
  const q = query.toLowerCase();
  return variables.filter(
    (v) => v && !isVariableFolder(v) && (v.friendlyName?.toLowerCase().includes(q) || v.name?.toLowerCase().includes(q))
  );
}

export function createVariablesTextInputPlugin(state: VariablePickerState): Plugin {
  return new Plugin({
    key: new PluginKey('variables-text-input'),
    props: {
      handleTextInput(view, _from, _to, text) {
        if (!SuggestionPluginKey.getState(view.state)?.active) return false;
        state.virtualQuery += text;
        if (state.latestProps) {
          const items = filterVariables(state.latestProps.editor.storage.variable.variables, state.virtualQuery);
          state.component?.updateProps({
            ...state.latestProps,
            query: state.virtualQuery,
            items,
          });
        }
        return true;
      },
    },
  });
}

const BaseVariablesExtensions = Node.create<BaseVariablesExtensionsProps, BaseVariablesExtensionsStorage>({
  name: 'variable',

  group: 'inline',

  inline: true,

  addOptions() {
    return {
      variables: [],
      disableSuggestions: false,
    };
  },
  addPasteRules() {
    return [
      new PasteRule({
        find: PVAR_REGEX,
        handler: ({ range, match, commands }) => {
          const rawJson = match[1]?.trim();
          if (!rawJson) return null;

          let payload: ClipboardVariablePayload;
          try {
            payload = JSON.parse(rawJson);
          } catch {
            return null;
          }

          const variableNode = payload.data as VariableNode;
          if (!variableNode?.name) return null;

          // An array-shaped node is the index-less field-schema entry emitted by
          // emitArrayFieldSchema; its objectId belongs to the array parent, not
          // to one instance, so it must not stamp a chip as an instance chip.
          const isPatternPayload = variableNode.shape?.type === 'array';

          commands.deleteRange(range);
          commands.insertVariable(
            {
              name: variableNode.name,
              friendlyName: variableNode.friendlyName,
              objectId: isPatternPayload ? null : variableNode.objectId ?? null,
              path: variableNode.path ?? null,
              template: variableNode.template ?? null,
            },
            { from: range.from, to: range.from }
          );
        },
      }),
    ];
  },

  addStorage() {
    return {
      variables: this.options.variables,
      onArrayVariableInserted: null,
    };
  },

  addAttributes() {
    return {
      name: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-name'),
        renderHTML: (attributes) => {
          if (!attributes.name) {
            return {};
          }

          return {
            'data-name': attributes.name,
          };
        },
      },
      friendlyName: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-friendly-name'),
        renderHTML: (attributes) => {
          if (!attributes.friendlyName) return {};
          return { 'data-friendly-name': attributes.friendlyName };
        },
      },
      objectId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-object-id'),
        renderHTML: (attributes) => {
          if (!attributes.objectId) return {};
          return { 'data-object-id': attributes.objectId };
        },
      },
      path: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-path'),
        renderHTML: (attributes) => {
          if (!attributes.path) return {};
          return { 'data-path': attributes.path };
        },
      },
      template: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-template'),
        renderHTML: (attributes) => {
          if (!attributes.template) return {};
          return { 'data-template': attributes.template };
        },
      },
      isConflict: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-is-conflict') === 'true',
        renderHTML: (attributes) => (attributes.isConflict ? { 'data-is-conflict': 'true' } : {}),
      },
      // Self-resolved chip: CRM array instances share one index-less name, so
      // per-instance chips in generated rows carry their value inline instead
      // of resolving through the store (which would yield the first instance).
      value: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-value'),
        renderHTML: (attributes) => {
          if (attributes.value === null || attributes.value === undefined) return {};
          return { 'data-value': attributes.value };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span',
        priority: 51, // TipTap's default priority is 50; Setting to DEFAULT + 1 so this rule comes first
        getAttrs: (element) => {
          const dataType = element.getAttribute('data-type');
          if (dataType !== this.name) return false;
          return {};
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    if (node.attrs.value !== null && node.attrs.value !== undefined) {
      return ['span', mergeAttributes({ 'data-type': this.name, class: 'tiptap-variable' }, HTMLAttributes), String(node.attrs.value)];
    }
    const variableEntity: VariableEntity | undefined = node.attrs.isConflict
      ? undefined
      : findVariableForChip(this.editor?.storage.variable.variables, node.attrs as VariableChipAttrs);
    const valueToRender = variableEntity ? formatVariable(node.attrs.name, variableEntity, this.options.documentCurrencyCode) : '';
    return ['span', mergeAttributes({ 'data-type': this.name, class: 'tiptap-variable' }, HTMLAttributes), valueToRender];
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ state, chain }) => {
          let isVariable = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isVariable = true;
              return chain().setNodeSelection(pos).run();
            }
          });

          return isVariable;
        }),
    };
  },
  addCommands() {
    return {
      insertVariable: (variable, commandRange) => {
        return ({ chain, editor }) => {
          const range = commandRange || {
            from: editor.state.selection.from,
            to: editor.state.selection.to,
          };

          // Check if next character is a space
          const nextChar = editor.state.doc.textBetween(range.to, range.to + 1);
          const needsSpace = nextChar !== ' ';

          const requested: VariableChipAttrs = typeof variable === 'string' ? { name: variable } : variable;
          const variableEntity = findVariableForChip<VariableEntity>(editor.storage.variable.variables, requested);
          const friendlyName = requested.friendlyName || variableEntity?.friendlyName || requested.name;
          const objectId = requested.objectId ?? null;
          const path = requested.path ?? null;
          const template = requested.template ?? null;
          const contentToInsert: Content = [
            { type: this.name, attrs: { name: requested.name, friendlyName, objectId, path, template, isConflict: requested.isConflict } },
          ];

          if (needsSpace) {
            contentToInsert.push({ type: 'text', text: ' ' });
          }

          return chain().focus().insertContentAt(range, contentToInsert).run();
        };
      },
    };
  },
  addProseMirrorPlugins() {
    if (this.options.disableSuggestions) {
      return [];
    }

    // Shared state between the suggestion render callbacks and the text-input interceptor.
    const suggestionState: VariablePickerState = {
      virtualQuery: '',
      latestProps: null,
      component: null,
    };

    const suggestionsPlugin = Suggestion<VariableEntity>({
      editor: this.editor,
      allowSpaces: false,
      char: '[',
      allow: ({ range, state }) => {
        const includesEndBrackets = state.doc.textBetween(range.from, range.to).includes(']');
        return !includesEndBrackets;
      },
      command: ({ props, range }) => {
        if (props.shape?.type === 'array') {
          const needsSpace = this.editor.state.doc.textBetween(range.to, range.to + 1) !== ' ';
          this.editor.storage.variable.onArrayVariableInserted?.(props, range, needsSpace);
          return true;
        }
        return this.editor.commands.insertVariable(
          {
            name: props.name,
            friendlyName: props.friendlyName,
            objectId: props.objectId ?? null,
            path: props.path ?? null,
            template: props.template ?? null,
          },
          range
        );
      },
      items: ({ query }: { query: string }) => {
        return filterVariables(this.editor.storage.variable.variables, query);
      },
      render: () => {
        const popup = tippy('body', {
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          theme: 'trigger-menu',
          getReferenceClientRect: () => {
            const coords = this.editor.view.coordsAtPos(this.editor.state.selection.$anchor.pos);

            return new DOMRect(coords.left, coords.top, 0, 0);
          },
          popperOptions: {
            modifiers: [
              {
                name: 'flip',
                enabled: false,
              },
            ],
          },
        });

        const tippyPopup: Instance = Array.isArray(popup) ? popup[0] : popup;

        return {
          onStart: (props: SuggestionProps) => {
            suggestionState.virtualQuery = '';
            suggestionState.latestProps = props;
            suggestionState.component = new ReactRenderer<VariablesListRef, SuggestionProps<VariableEntity>>(VariablesList, {
              props,
              editor: props.editor,
            });

            const getReferenceClientRect = () => {
              const coords = this.editor.view.coordsAtPos(this.editor.state.selection.$anchor.pos);

              return new DOMRect(coords.left, coords.top, 0, 0);
            };

            tippyPopup?.setProps({
              getReferenceClientRect,
              appendTo: document.body,
              content: suggestionState.component?.element,
            });

            tippyPopup?.show();
          },

          onUpdate(props: SuggestionProps) {
            suggestionState.latestProps = props;
            const updatedItems = filterVariables(props.editor.storage.variable.variables, suggestionState.virtualQuery);
            suggestionState.component?.updateProps({ ...props, query: suggestionState.virtualQuery, items: updatedItems });

            const { view, state } = props.editor;

            const getReferenceClientRect = () => {
              const coords = view.coordsAtPos(state.selection.$anchor.pos);

              return new DOMRect(coords.left, coords.top, 0, 0);
            };

            tippyPopup?.setProps({
              getReferenceClientRect,
            });
          },

          onKeyDown(props: SuggestionKeyDownProps) {
            if (props.event.key === 'Escape') {
              tippyPopup?.hide();
              return true;
            }

            // Enter with no query typed — dismiss the popup without inserting a variable.
            if (props.event.key === 'Enter' && suggestionState.virtualQuery.length === 0) {
              tippyPopup?.hide();
              return true;
            }

            // Remove the last character from virtualQuery on Backspace without
            // deleting from the document. When virtualQuery is empty, fall through
            // so TipTap can delete `[` and trigger onExit naturally.
            if (props.event.key === 'Backspace' && suggestionState.virtualQuery.length > 0) {
              suggestionState.virtualQuery = suggestionState.virtualQuery.slice(0, -1);
              if (suggestionState.latestProps) {
                const items = filterVariables(suggestionState.latestProps.editor.storage.variable.variables, suggestionState.virtualQuery);
                suggestionState.component?.updateProps({
                  ...suggestionState.latestProps,
                  query: suggestionState.virtualQuery,
                  items,
                });
              }
              return true;
            }

            if (!tippyPopup?.state.isShown) {
              tippyPopup?.show();
            }

            return suggestionState.component?.ref?.onKeyDown(props) ?? false;
          },

          onExit() {
            suggestionState.virtualQuery = '';
            suggestionState.latestProps = null;
            tippyPopup?.hide();
            if (suggestionState.component) {
              suggestionState.component.destroy();
              suggestionState.component = null;
            }
          },
        };
      },
    });

    const textInputPlugin = createVariablesTextInputPlugin(suggestionState);

    return [suggestionsPlugin, textInputPlugin];
  },
});

export const PreviewVariables = BaseVariablesExtensions.extend({
  atom: false,
  selectable: false,

  addNodeView() {
    return ReactNodeViewRenderer(PreviewVariableNodeView, { className: 'tiptap-variable-preview' });
  },
});

export const PreviewContentVariables = BaseVariablesExtensions.extend({
  atom: false,
  selectable: false,

  addNodeView() {
    return ReactNodeViewRenderer(PreviewContentVariableNodeView, { className: 'tiptap-variable-preview' });
  },
});

export const EditorVariables = BaseVariablesExtensions.extend({
  atom: true,
  selectable: true,

  addNodeView() {
    return ReactNodeViewRenderer(VariableNodeView);
  },
});
